import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface AuthState {
  user: any | null;
  orgId: string | null;
  isPlatformOwner: boolean;
  loading: boolean;
  error: string | null;
}

/**
 * Centralized authentication and organization fetching hook
 * Replaces 58+ duplicate auth patterns across 44 files
 */
export const useAuth = () => {
  const [state, setState] = useState<AuthState>({
    user: null,
    orgId: null,
    isPlatformOwner: false,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let isMounted = true;

    const fetchAuth = async () => {
      try {
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        
        if (authError) throw authError;
        if (!user) {
          if (isMounted) {
            setState({ user: null, orgId: null, isPlatformOwner: false, loading: false, error: "Not authenticated" });
          }
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('org_id')
          .eq('id', user.id)
          .single();

        if (profileError) throw profileError;

        // Check if user is platform owner (has owner role with NULL org_id)
        const { data: platformOwnerRole } = await supabase
          .from('user_roles')
          .select('role')
          .eq('user_id', user.id)
          .eq('role', 'owner')
          .is('org_id', null)
          .single();

        if (isMounted) {
          setState({
            user,
            orgId: profile?.org_id || null,
            isPlatformOwner: !!platformOwnerRole,
            loading: false,
            error: null,
          });
        }
      } catch (err: any) {
        console.error("Auth error:", err);
        if (isMounted) {
          setState({
            user: null,
            orgId: null,
            isPlatformOwner: false,
            loading: false,
            error: err.message,
          });
        }
        toast.error("Authentication failed");
      }
    };

    fetchAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  return state;
};
